import { Observable, take } from 'rxjs';

import {
  Container,
  Service,
  ILearningExperience,
  CourseTrack,
} from '../../../symphony';

import {
  IAnswerExplanationData,
  IMessages,
  IQuestionData,
} from '../global-interface';
import { IResponse } from '../../../api/private/global.interface';
import { ILearnEngineApi, CodeAssistantConfigType } from '../../../api/private/learn-engine-api/learn-engine-api.interface';
import {
  IAIChatApi, AIChatApi, IAIAssistantResponse, Ratings,
} from '../../../api/public/ai-chat-api';
import {
  ICodeAssistantApi, CodeAssistantApi, ICode, ICodeAssistantRes, AttemptResType, ContextAttempt,
} from '../../../api/public/code-assistant';
import { LearnEngineApi } from '../../../api/public/learn-engine/learn-engine-api';

@Service()
export class SlApiService {
  private aIChatApi: IAIChatApi;

  private learnEgineApi: ILearnEngineApi;

  private codeAssistantApi: ICodeAssistantApi;

  constructor() {
    this.aIChatApi = Container.take('global', AIChatApi);
    this.learnEgineApi = Container.take('global', LearnEngineApi);
    this.codeAssistantApi = Container.take('global', CodeAssistantApi);
  }

  public getAIAssistantResponse(
    messageHistory: IMessages[],
    conversationId: string,
    systemPromptId: string,
    isTestUser: boolean,
  ): Observable<IAIAssistantResponse> {
    return this.aIChatApi
      .getAIAssistantResponse(
        messageHistory,
        conversationId,
        systemPromptId,
        isTestUser,
      )
      .pipe(take(1));
  }

  public getLearningExperiences(): Observable<ILearningExperience[]> {
    return this.learnEgineApi.getLearningExperiences().pipe(take(1));
  }

  public rateMessage(
    messageId: string,
    rating: Ratings,
  ): Observable<IResponse> {
    return this.aIChatApi.rateMessage(messageId, rating).pipe(take(1));
  }

  public getCodeAssistantConfig(): Observable<CodeAssistantConfigType[]> {
    return this.learnEgineApi.getCodeAssistantConfig().pipe(take(1));
  }

  public explainTheCode(codes: ICode[], track: CourseTrack): Observable<ICodeAssistantRes> {
    return this.codeAssistantApi.explainTheCode(codes, track).pipe(take(1));
  }

  public findBug(
    codes: ICode[],
    problem: string,
    track: CourseTrack
  ): Observable<ICodeAssistantRes> {
    return this.codeAssistantApi.findBug(codes, problem, track).pipe(take(1));
  }

  public hint(
    codes: ICode[],
    problem: string,
    track: CourseTrack
  ): Observable<ICodeAssistantRes> {
    return this.codeAssistantApi.codeHint(codes, problem, track).pipe(take(1));
  }

  public alternativeLessonExplanation(
    courseName: string,
    content: string,
    alias: string,
  ): Observable<ICodeAssistantRes> {
    return this.codeAssistantApi
      .alternativeLessonExplanation(courseName, content, alias)
      .pipe(take(1));
  }

  public realLifeExamples(
    courseName: string,
    content: string,
    alias: string,
  ): Observable<ICodeAssistantRes> {
    return this.codeAssistantApi
      .realLifeExamples(courseName, content, alias)
      .pipe(take(1));
  }

  public questionHint(
    courseName: string,
    question: IQuestionData,
    alias: string,
  ): Observable<ICodeAssistantRes> {
    return this.codeAssistantApi
      .questionHint({ ...question, courseName, alias })
      .pipe(take(1));
  }

  public rateAISolution(id: string, rating: number): Observable<string> {
    return this.codeAssistantApi.rate(id, rating).pipe(take(1));
  }

  public getAttemptData(): Observable<AttemptResType[]> {
    return this.codeAssistantApi.getAttemptData().pipe(take(1));
  }

  public attempt(context: number, track: CourseTrack): Observable<ContextAttempt> {
    return this.codeAssistantApi.attempt(context, track).pipe(take(1));
  }

  public use(context: number, track: CourseTrack): Observable<AttemptResType[]> {
    return this.codeAssistantApi.use(context, track).pipe(take(1));
  }

  public getSolution(
    problem: string,
    encryptedCode: string,
    language: number,
    track: CourseTrack
  ): Observable<ICodeAssistantRes> {
    return this.codeAssistantApi
      .getSolution(problem, encryptedCode, language, track)
      .pipe(take(1));
  }

  public answerExplanation(
    answerExplanationData: IAnswerExplanationData,
  ) {
    return this.learnEgineApi.explainAsnwer({
      dynamicId: answerExplanationData.dynamicId,
      answers: answerExplanationData.answerOptions,
      isCorrect: answerExplanationData.isCorrect
    });
  }
}
